import { getCsrfToken } from "@/lib/csrf";
import { DEFAULT_SLOT_MINUTES } from "./constants";
import type {
	AvailableTherapistsParams,
	TherapistAvailabilityResponse,
	TherapistCalendarParams,
	TherapistCalendarResponse,
	TherapistSlotsParams,
	TherapistSlotsResponse,
} from "./types";

/** Serialize defined params; skip undefined/null/empty (and `false`, which upstream
 *  reads as the default anyway — so an off flag stays out of the URL and out of the
 *  in-flight dedup key). */
export function buildQuery(
	params: Record<string, string | number | boolean | undefined>,
): string {
	const sp = new URLSearchParams();
	for (const [k, v] of Object.entries(params)) {
		if (v !== undefined && v !== null && v !== "" && v !== false) {
			sp.set(k, String(v));
		}
	}
	return sp.toString();
}

async function rawGet<T>(url: string, signal: AbortSignal): Promise<T> {
	const response = await fetch(url, {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
			"X-CSRF-Token": getCsrfToken(),
		},
		signal,
	});
	if (!response.ok) {
		// Surface the upstream error message (e.g. "missing required fields:
		// location coordinates") instead of a bare status code.
		let detail = "";
		try {
			const body = (await response.json()) as {
				message?: string;
				error?: string;
			};
			detail = body?.message ?? body?.error ?? "";
		} catch {
			// Non-JSON / empty error body — fall back to the status code.
		}
		throw new Error(
			detail
				? `${detail} (${response.status})`
				: `Request failed (${response.status})`,
		);
	}
	return (await response.json()) as T;
}

type InflightEntry = {
	promise: Promise<unknown>;
	controller: AbortController;
	refCount: number;
};

// Coalesce identical GETs that are in flight at the same time: concurrent
// callers for the same URL share one network request. A per-caller AbortSignal
// still cancels: each caller ref-counts the shared request and the underlying
// fetch is only aborted once ALL interested callers have aborted. This composes
// with the hooks' abort-on-supersede/unmount without double-fetching.
const inflight = new Map<string, InflightEntry>();

export function authGet<T>(
	url: string,
	opts?: { signal?: AbortSignal },
): Promise<T> {
	const signal = opts?.signal;

	let entry = inflight.get(url);
	// Never coalesce onto a request that's already been aborted: its rejection
	// is in flight and would wrongly surface to this new caller. Start fresh.
	if (entry?.controller.signal.aborted) entry = undefined;
	if (!entry) {
		const controller = new AbortController();
		const fresh: InflightEntry = {
			controller,
			refCount: 0,
			promise: rawGet<T>(url, controller.signal),
		};
		fresh.promise
			.finally(() => {
				if (inflight.get(url) === fresh) inflight.delete(url);
			})
			// Swallow the rejection on THIS cleanup chain only — the real error is
			// still delivered to callers via `shared.promise.then(...)` below.
			// Without this, a rejected shared request leaks an unhandled rejection.
			.catch(() => {});
		inflight.set(url, fresh);
		entry = fresh;
	}
	const shared = entry;
	shared.refCount += 1;

	return new Promise<T>((resolve, reject) => {
		let done = false;
		const finish = (settle: () => void) => {
			if (done) return;
			done = true;
			signal?.removeEventListener("abort", onAbort);
			shared.refCount -= 1;
			if (shared.refCount <= 0) {
				// Nobody is waiting: cancel the underlying request AND drop it from
				// the map synchronously, so a caller arriving in the same tick starts
				// a fresh request instead of coalescing onto this dying one.
				shared.controller.abort();
				if (inflight.get(url) === shared) inflight.delete(url);
			}
			settle();
		};
		const onAbort = () =>
			finish(() =>
				reject(signal?.reason ?? new DOMException("Aborted", "AbortError")),
			);

		if (signal?.aborted) {
			onAbort();
			return;
		}
		signal?.addEventListener("abort", onAbort);
		shared.promise.then(
			(res) => finish(() => resolve(res as T)),
			(err) => finish(() => reject(err as Error)),
		);
	});
}

export function getAvailableTherapists(
	params: AvailableTherapistsParams,
	opts?: { signal?: AbortSignal },
): Promise<TherapistAvailabilityResponse> {
	return authGet(
		`/api/v1/therapists/available?${buildQuery({ ...params, slot_minutes: DEFAULT_SLOT_MINUTES })}`,
		opts,
	);
}

export function getTherapistSlots(
	therapistId: string,
	params: TherapistSlotsParams,
	opts?: { signal?: AbortSignal },
): Promise<TherapistSlotsResponse> {
	return authGet(
		`/api/v1/therapists/${encodeURIComponent(therapistId)}/slots?${buildQuery({ ...params, slot_minutes: DEFAULT_SLOT_MINUTES })}`,
		opts,
	);
}

export function getTherapistCalendar(
	therapistId: string,
	params: TherapistCalendarParams,
	opts?: { signal?: AbortSignal },
): Promise<TherapistCalendarResponse> {
	return authGet(
		`/api/v1/therapists/${encodeURIComponent(therapistId)}/availability/calendar?${buildQuery({ ...params, slot_minutes: DEFAULT_SLOT_MINUTES })}`,
		opts,
	);
}
